import React, { PropsWithChildren } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { NavBar as NavBarComponent } from 'component-library-next'
import { concatAll } from 'ramda-adjunct'
import { goBack } from 'redux-first-history'

import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { useDialog } from '@app/lib/disclosure/Dialog/useDialog'
import { useAppointmentsNavbarEntries } from '@app/modules/Appointments'
import { useAuditReportsNavbarEntries } from '@app/modules/AuditReports'
import AuthAction from '@app/modules/Auth/actions'
import { useCourtCasesTablesNavbarEntries } from '@app/modules/CourtCasesTables'
import { useClientsListNavbarEntries } from '@app/modules/CustomerList'
import { useLatestInteractionsNavbarEntries } from '@app/modules/LatestInteractions'
import { useOverviewNavbarEntries } from '@app/modules/Overview'
import { usePostponedLetterRequestsNavbarEntries } from '@app/modules/PostponedLetterRequests'
import { ReportProblemDialog, ReportProblemNavBarEntry } from '@app/modules/ReportProblem'
import { useStatsNavbarEntries } from '@app/modules/StatsDashboard'
import { currentRouteSelector } from '@app/Router/selectors/selectors'

import type { NavBarEntry } from './NavBarEntry'

export function NavBar({ children, title }: PropsWithChildren<{ title: string }>) {
  const dispatch = useDispatch()
  const currentRoute = useSelector(currentRouteSelector)
  const isLawyerDashboardEnabled = useSelector(isFeatureEnabled(Features.LAWYER_DASHBOARD))
  const reportProblemDialog = useDialog()

  const overviewNavbarEntries = useOverviewNavbarEntries()
  const appointmentsNavbarEntries = useAppointmentsNavbarEntries()
  const auditReportsNavbarEntries = useAuditReportsNavbarEntries()
  const clientsListNavbarEntries = useClientsListNavbarEntries()
  const postponedLetterRequestsNavbarEntries = usePostponedLetterRequestsNavbarEntries()
  const statsEntries = !!isLawyerDashboardEnabled ? useStatsNavbarEntries() : []
  const latestInteractions = useLatestInteractionsNavbarEntries()
  const courtCasesTablesEntries = useCourtCasesTablesNavbarEntries()

  const entries = concatAll<NavBarEntry[]>([
    overviewNavbarEntries,
    clientsListNavbarEntries,
    auditReportsNavbarEntries,
    appointmentsNavbarEntries,
    postponedLetterRequestsNavbarEntries,
    statsEntries,
    latestInteractions,
    courtCasesTablesEntries
  ])

  return (
    <>
      <NavBarComponent
        entries={(entries || []).map((entry) => ({
          ...entry,
          active: entry.active.test(currentRoute)
        }))}
        notificationArea={<ReportProblemNavBarEntry onClick={reportProblemDialog.openDialog} />}
        onBack={(to) => dispatch(goBack(to))}
        onLogOut={() => {
          dispatch(AuthAction.logout())
        }}
        title={title}
      >
        {children}
      </NavBarComponent>
      <ReportProblemDialog
        dialogProps={reportProblemDialog.dialogProps}
        closeDialog={reportProblemDialog.closeDialog}
      />
    </>
  )
}
